/*
 * Copy Measurement Point Tab Form
 * Pick a destination measurement point list (Equipment Type + Type + Standard)
 * and copy the selected tab into it, optionally under a different name.
 */

import * as React from 'react';
import {
    FormGenerator,
    AbstractControl,
    FieldConfig,
    GroupProps,
    FormGroup
} from 'react-reactive-form';
import { Alert, Button, Col } from 'react-bootstrap';
import { toastr } from 'react-redux-toastr';
import { TFunction } from 'i18next';

import { FormUtil } from '../common/FormUtil';
import { constants } from '../../constants/constants';
import {
    Ioption,
    ImeasurementPointList,
    ImeasurementPointListSummary,
    ImeasurementPointListTab
} from '../../models';
import { measurementPointListTypeEnum } from '../../models-enums';
import { getMeasurementPointList } from '../../actions/manageMeasurementPointListsActions';
import {
    availableValuesFor,
    IcopyTabTargetSelection,
    pruneInvalidatedSelection,
    resolveTargetMeasurementPointListID
} from './copyTabUtil';

interface Iprops {
    t: TFunction;
    loading: boolean;
    colorButton: string;
    toggleModal: () => void;
    selectedTab: ImeasurementPointListTab;
    selectedMeasurementPointList: ImeasurementPointList;
    summaries: ImeasurementPointListSummary[];
    mainCategoryOptions: Ioption[];
    standardOptions: Ioption[];
    getMeasurementPointListSummaries: () => Promise<void>;
    copyMeasurementPointTab: (
        sourceTabID: string,
        targetMeasurementPointListID: string,
        tabName: string
    ) => Promise<any>;
    getMeasurementPointList: typeof getMeasurementPointList;
}

interface Istate {
    selection: IcopyTabTargetSelection;
    tabName: string;
    fieldConfig: FieldConfig;
    duplicateName: boolean;
}

export class CopyMeasurementPointTabForm extends React.Component<
    Iprops,
    Istate
> {
    public formGroup: FormGroup | any;
    private subscriptions: any[] = [];

    constructor(props: Iprops) {
        super(props);
        // The source tab is already selected by the time the modal opens, so its name is the
        // prefill for the copy. componentDidUpdate reseeds if the source tab ever changes.
        const tabName = props.selectedTab.name || '';
        this.state = {
            selection: {},
            tabName,
            fieldConfig: FormUtil.translateForm(
                this.buildFieldConfig({}, tabName),
                this.props.t
            ),
            duplicateName: false
        };
    }

    componentDidMount() {
        this.props
            .getMeasurementPointListSummaries()
            .catch((error: any) => console.error(error));
    }

    componentDidUpdate(prevProps: Iprops) {
        // A different source tab means the prefilled name belongs to the old tab. Reseed rather
        // than copy tab B's points under tab A's name.
        if (prevProps.selectedTab.id !== this.props.selectedTab.id) {
            this.setState({ tabName: this.props.selectedTab.name || '' }, () =>
                this.rebuildFieldConfig(this.state.selection)
            );
            return;
        }

        // Summaries arrive after mount, so the dropdowns start empty and fill in.
        if (
            JSON.stringify(prevProps.summaries) !==
            JSON.stringify(this.props.summaries)
        ) {
            this.rebuildFieldConfig(this.state.selection);
        }
    }

    componentWillUnmount() {
        this.unsubscribe();
    }

    unsubscribe = () => {
        this.subscriptions.forEach(subscription => {
            if (subscription) {
                subscription.unsubscribe();
            }
        });
        this.subscriptions = [];
    };

    /*
     * Each dropdown offers only values that keep at least one destination list alive,
     * given the other two selections. Type options come from the Web enum intersected
     * with what the API actually returned, so a list type this build does not know
     * about is simply never offered rather than rendering a blank label.
     */
    buildFieldConfig = (
        selection: IcopyTabTargetSelection,
        tabName: string
    ) => {
        const sourceID = this.props.selectedMeasurementPointList.id;
        const summaries = this.props.summaries || [];

        const allowedMainCategories = availableValuesFor(
            'mainCategoryID',
            summaries,
            sourceID,
            selection
        );
        const allowedTypes = availableValuesFor(
            'type',
            summaries,
            sourceID,
            selection
        );
        const allowedStandards = availableValuesFor(
            'standardID',
            summaries,
            sourceID,
            selection
        );

        const mainCategoryOptions = this.props.mainCategoryOptions.filter(
            (option: Ioption) =>
                allowedMainCategories.indexOf(option.value) > -1
        );
        const standardOptions = this.props.standardOptions.filter(
            (option: Ioption) => allowedStandards.indexOf(option.value) > -1
        );
        // Ioption.value is typed string but holds a number for enum options - hence Number().
        const typeOptions = FormUtil.convertEnumToOptions(
            measurementPointListTypeEnum
        ).filter(
            (option: Ioption) => allowedTypes.indexOf(Number(option.value)) > -1
        );

        const selectedMainCategory =
            mainCategoryOptions.find(
                (option: Ioption) => option.value === selection.mainCategoryID
            ) || null;
        // FormUtil.translateForm rewrites meta.options but never formState.value, so the
        // selected option has to carry its own translated label - otherwise the box shows
        // the raw enum key ('annual') instead of the display name ('Inspection').
        const selectedTypeOption = typeOptions.find(
            (option: Ioption) => Number(option.value) === selection.type
        );
        const selectedType = selectedTypeOption
            ? {
                  ...selectedTypeOption,
                  label: this.props.t(selectedTypeOption.label)
              }
            : null;
        const selectedStandard =
            standardOptions.find(
                (option: Ioption) => option.value === selection.standardID
            ) || null;

        const fieldConfigControls = {
            tabName: {
                options: {
                    validators: [FormUtil.validators.requiredWithTrim]
                },
                render: FormUtil.TextInput,
                meta: {
                    label: 'measurementPointTabLabel',
                    colWidth: 12,
                    name: 'copy-measurement-point-tab-name'
                },
                formState: { value: tabName, disabled: false }
            },
            mainCategoryID: {
                render: FormUtil.SelectWithoutValidation,
                meta: {
                    options: mainCategoryOptions,
                    label: 'manageMeasurementPointLists:equipmentType',
                    required: true,
                    colWidth: 12,
                    placeholder:
                        'manageMeasurementPointLists:equipmentTypePlaceholder',
                    isClearable: true
                },
                formState: { value: selectedMainCategory, disabled: false }
            },
            type: {
                render: FormUtil.SelectWithoutValidation,
                meta: {
                    options: typeOptions,
                    label: 'manageMeasurementPointLists:type',
                    required: true,
                    colWidth: 12,
                    placeholder: 'manageMeasurementPointLists:typePlaceholder',
                    shouldTranslate: true,
                    isClearable: true
                },
                formState: { value: selectedType, disabled: false }
            },
            standardID: {
                render: FormUtil.SelectWithoutValidation,
                meta: {
                    options: standardOptions,
                    label: 'manageMeasurementPointLists:standard',
                    required: true,
                    colWidth: 12,
                    placeholder:
                        'manageMeasurementPointLists:standardPlaceholder',
                    isClearable: true
                },
                formState: { value: selectedStandard, disabled: false }
            }
        } as { [key: string]: GroupProps };

        return { controls: { ...fieldConfigControls } } as FieldConfig;
    };

    /*
     * Rebuilding remounts every control, so the name typed so far is fed back in from state -
     * otherwise changing a dropdown would reset the name box.
     */
    rebuildFieldConfig = (selection: IcopyTabTargetSelection) => {
        this.setState({
            selection,
            duplicateName: false,
            fieldConfig: FormUtil.translateForm(
                this.buildFieldConfig(selection, this.state.tabName),
                this.props.t
            )
        });
    };

    setForm = (form: AbstractControl) => {
        this.formGroup = form;
        this.formGroup.meta = { loading: this.props.loading };
        // Rebuilding fieldConfig remounts the controls, so drop the old
        // subscriptions before adding new ones or handlers stack up.
        this.unsubscribe();
        this.subscribeToValueChanges();
    };

    subscribeToValueChanges = () => {
        ['mainCategoryID', 'type', 'standardID'].forEach((key: string) => {
            const subscription = this.formGroup
                .get(key)
                ?.valueChanges.subscribe((value: Ioption | null) => {
                    this.handleValueChange(key, value);
                });
            this.subscriptions.push(subscription);
        });

        const nameSubscription = this.formGroup
            .get('tabName')
            ?.valueChanges.subscribe((value: string) => {
                this.handleNameChange(value);
            });
        this.subscriptions.push(nameSubscription);
    };

    /*
     * Kept out of rebuildFieldConfig on purpose: rebuilding the field config remounts the input,
     * which would drop focus on every keystroke. A new name also clears a duplicate-name warning,
     * since renaming is how that conflict gets resolved.
     */
    handleNameChange = (value: string) => {
        this.setState({ tabName: value || '', duplicateName: false });
    };

    handleValueChange = (key: string, value: Ioption | null) => {
        const nextSelection = { ...this.state.selection };

        if (key === 'type') {
            nextSelection.type = value ? Number(value.value) : undefined;
        } else if (key === 'mainCategoryID') {
            nextSelection.mainCategoryID = value ? value.value : undefined;
        } else {
            nextSelection.standardID = value ? value.value : undefined;
        }

        // Clear anything the new pick invalidated, so the dropdowns never disagree
        // with the selection held in state.
        const pruned = pruneInvalidatedSelection(
            this.props.summaries || [],
            this.props.selectedMeasurementPointList.id,
            nextSelection
        );

        if (JSON.stringify(pruned) === JSON.stringify(this.state.selection)) {
            return;
        }

        this.rebuildFieldConfig(pruned);
    };

    targetMeasurementPointListID = () =>
        resolveTargetMeasurementPointListID(
            this.props.summaries || [],
            this.props.selectedMeasurementPointList.id,
            this.state.selection
        );

    /*
     * Human-readable destination, built from the same option arrays the dropdowns use
     * so there is one source of truth for names.
     */
    destinationLabel = () => {
        const { selection } = this.state;

        const mainCategory = this.props.mainCategoryOptions.find(
            (option: Ioption) => option.value === selection.mainCategoryID
        );
        const standard = this.props.standardOptions.find(
            (option: Ioption) => option.value === selection.standardID
        );
        const type =
            selection.type !== undefined
                ? this.props.t(measurementPointListTypeEnum[selection.type])
                : '';

        return [
            type,
            mainCategory && mainCategory.label,
            standard && standard.label
        ]
            .filter(part => Boolean(part))
            .join(' - ');
    };

    handleSubmit = (e: React.MouseEvent<HTMLFormElement>) => {
        e.preventDefault();

        const targetID = this.targetMeasurementPointListID();
        const tabName = this.state.tabName.trim();
        if (!targetID || !tabName) {
            return;
        }

        this.props
            .copyMeasurementPointTab(
                this.props.selectedTab.id,
                targetID,
                tabName
            )
            .then(() => {
                toastr.success(
                    this.props.t('common:success'),
                    this.props.t('copyTabSuccess'),
                    constants.toastrSuccess
                );
                // The destination list in redux predates the new tab.
                this.props.getMeasurementPointList(targetID);
                this.props.toggleModal();
            })
            .catch((error: any) => {
                if (error && error.response && error.response.status === 409) {
                    this.setState({ duplicateName: true });
                    return;
                }
                constants.handleError(error, 'copy measurement point tab');
            });
    };

    render() {
        const { t } = this.props;
        const targetID = this.targetMeasurementPointListID();

        return (
            <form
                onSubmit={this.handleSubmit}
                className={`clearfix beacon-form ${this.props.colorButton}`}
            >
                {/*
                 * No Row wrappers: FormGenerator renders its controls as bare Cols, so a Row's
                 * negative margins would pull this text 15px left of the inputs.
                 */}
                <Col xs={12}>
                    <h5 className="list-label">
                        {t('copyTabSourceLabel')}: {this.props.selectedTab.name}
                    </h5>
                </Col>

                <FormGenerator
                    onMount={this.setForm}
                    fieldConfig={this.state.fieldConfig}
                />

                <Col xs={12}>
                    {targetID ? (
                        <p>
                            <strong>{t('copyTabDestinationLabel')}:</strong>{' '}
                            {this.destinationLabel()}
                        </p>
                    ) : (
                        <p className="text-muted">
                            {t('copyTabNoDestination')}
                        </p>
                    )}
                    {this.state.duplicateName && (
                        <Alert bsStyle="warning">
                            {t('copyTabDuplicateName')}
                        </Alert>
                    )}
                </Col>

                <Col xs={12} className="form-buttons text-right">
                    <Button
                        bsStyle="default"
                        type="button"
                        className="pull-left"
                        onClick={this.props.toggleModal}
                    >
                        {t('common:cancel')}
                    </Button>
                    <Button
                        bsStyle={this.props.colorButton}
                        type="submit"
                        disabled={
                            this.props.loading ||
                            !targetID ||
                            !this.state.tabName.trim()
                        }
                    >
                        {t('common:copy')}
                    </Button>
                </Col>
            </form>
        );
    }
}
